import React, { useEffect, useState } from 'react';
import {
  Button,
  ComposedModal,
  Form,
  ModalBody,
  ModalFooter,
  ModalHeader,
  MultiSelect,
  Select,
  SelectItem,
  TextInput,
  TextArea,
  DatePicker,
  DatePickerInput,
  Toggle,
  InlineNotification,
  Stack,
} from '@carbon/react';

import { useGetGroupsQuery } from '@/redux/new_services/groups';
import { useCreateNewsMutation, useUpdateNewsMutation } from '@/redux/new_services/news';
import { RichTextEditor } from '@/components/RichTextEditor';
import { uploadNewsImageInline, uploadNewsVideoInline } from './uploadHelper';
import TeaserImagePicker from './TeaserImagePicker';

const CATEGORIES = [
  { value: 'info', label: 'Information' },
  { value: 'release', label: 'Release / Neue Funktion' },
  { value: 'warning', label: 'Warnung' },
  { value: 'announcement', label: 'Ankündigung' },
];

const blank = () => ({
  title: '',
  excerpt: '',
  content: '',
  category: 'info',
  is_pinned: false,
  teaser_image_id: null,
  groups: [],
  published_at: null,
  expires_at: null,
});

// Convert YYYY-MM-DD to ISO timestamp at start of day; null if empty.
const toIsoDay = (d) => (d ? new Date(d).toISOString() : null);
// Convert ISO timestamp back to YYYY-MM-DD for the DatePickerInput value.
const fromIsoDay = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

// NewsForm is the create + edit modal. It hosts the rich text editor, the
// teaser-image picker, group multi-select and publish/expire dates.
const NewsForm = ({ open, news, onClose }) => {
  const [state, setState] = useState(blank());
  const [error, setError] = useState(null);

  const { data: groupsResp } = useGetGroupsQuery();
  const groups = groupsResp?.data || [];

  const [createNews, { isLoading: creating }] = useCreateNewsMutation();
  const [updateNews, { isLoading: updating }] = useUpdateNewsMutation();
  const isEdit = Boolean(news?.model?.ID);
  const isSaving = creating || updating;

  // Re-seed form whenever the modal is (re)opened.
  useEffect(() => {
    if (!open) return;
    setError(null);
    if (news) {
      setState({
        title: news.title || '',
        excerpt: news.excerpt || '',
        content: news.content || '',
        category: news.category || 'info',
        is_pinned: !!news.is_pinned,
        teaser_image_id: news.teaser_image_id || null,
        groups: news.groups || [],
        published_at: news.published_at || null,
        expires_at: news.expires_at || null,
      });
    } else {
      setState(blank());
    }
  }, [open, news]);

  const setField = (key, value) => setState((s) => ({ ...s, [key]: value }));

  const handleSave = async () => {
    if (!state.title.trim()) {
      setError('Titel ist erforderlich');
      return;
    }
    const payload = {
      ...state,
      title: state.title.trim(),
    };
    try {
      if (isEdit) {
        await updateNews({ id: news.model.ID, ...payload }).unwrap();
      } else {
        await createNews(payload).unwrap();
      }
      onClose?.();
    } catch (e) {
      setError(e?.data?.message || 'Speichern fehlgeschlagen');
    }
  };

  // Publish-now button: stamps published_at = now (only if currently null).
  const publishNow = () => {
    if (state.published_at) return;
    setField('published_at', new Date().toISOString());
  };
  const unpublish = () => setField('published_at', null);

  return (
    <ComposedModal open={open} onClose={onClose} size="lg">
      <ModalHeader title={isEdit ? 'Nachricht bearbeiten' : 'Neue Nachricht'} />
      <ModalBody hasForm>
        <Form>
          <Stack gap={5}>
            {error && (
              <InlineNotification
                kind="error"
                title="Fehler"
                subtitle={error}
                onCloseButtonClick={() => setError(null)}
                lowContrast
              />
            )}

            <TextInput
              id="news-title"
              labelText="Titel"
              value={state.title}
              onChange={(e) => setField('title', e.target.value)}
              required
            />

            <TextArea
              id="news-excerpt"
              labelText="Teaser-Text (optional, kurze Vorschau)"
              value={state.excerpt}
              onChange={(e) => setField('excerpt', e.target.value)}
              rows={2}
              maxCount={280}
              enableCounter
            />

            <div>
              <label className="cds--label">Teaser-Bild</label>
              <TeaserImagePicker
                mediaId={state.teaser_image_id}
                onChange={(id) => setField('teaser_image_id', id)}
              />
            </div>

            <div>
              <label className="cds--label">Inhalt</label>
              <RichTextEditor
                value={state.content}
                onChange={(html) => setField('content', html)}
                placeholder="Schreib hier den vollen Artikel-Text. Bilder, Videos, Tabellen, Listen, Links sind möglich."
                onUploadImage={uploadNewsImageInline}
                onUploadVideo={uploadNewsVideoInline}
              />
            </div>

            <Select
              id="news-category"
              labelText="Kategorie"
              value={state.category}
              onChange={(e) => setField('category', e.target.value)}
            >
              {CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value} text={c.label} />
              ))}
            </Select>

            <MultiSelect
              id="news-groups"
              titleText="Sichtbar für Gruppen (leer = für alle)"
              label="Gruppen wählen"
              items={groups}
              itemToString={(g) => (g ? g.name : '')}
              initialSelectedItems={state.groups}
              selectedItems={state.groups}
              onChange={({ selectedItems }) => setField('groups', selectedItems)}
            />

            <Toggle
              id="news-pinned"
              labelText="Oben anpinnen"
              labelA="Aus"
              labelB="An"
              toggled={state.is_pinned}
              onToggle={(v) => setField('is_pinned', v)}
            />

            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
              <DatePicker
                datePickerType="single"
                value={fromIsoDay(state.published_at)}
                onChange={(values) => setField('published_at', values[0] ? values[0].toISOString() : null)}
              >
                <DatePickerInput
                  id="news-published"
                  labelText="Veröffentlichen ab"
                  placeholder="JJJJ-MM-TT"
                />
              </DatePicker>
              <DatePicker
                datePickerType="single"
                value={fromIsoDay(state.expires_at)}
                onChange={(values) => setField('expires_at', values[0] ? values[0].toISOString() : null)}
              >
                <DatePickerInput
                  id="news-expires"
                  labelText="Ablauf am (optional)"
                  placeholder="JJJJ-MM-TT"
                />
              </DatePicker>
              {state.published_at ? (
                <Button kind="ghost" onClick={unpublish}>Entwurf machen</Button>
              ) : (
                <Button kind="ghost" onClick={publishNow}>Jetzt veröffentlichen</Button>
              )}
            </div>
          </Stack>
        </Form>
      </ModalBody>
      <ModalFooter
        primaryButtonText={isSaving ? 'Speichern …' : 'Speichern'}
        primaryButtonDisabled={isSaving}
        secondaryButtonText="Abbrechen"
        onRequestClose={onClose}
        onRequestSubmit={handleSave}
      />
    </ComposedModal>
  );
};

export default NewsForm;
